import Service, { service } from "@ember/service";
import type IntlService from "ember-intl/services/intl";
import emberSwal from "ui-kit/utils/ember-swal";

interface Error {
  title: string;
  detail: string;
  code: string;
  source: string;
  status: string;
}

interface Errors {
  errors: Error[];
}

export default class ErrorsService extends Service {
  @service declare intl: IntlService;

  displayError(e: string) {
    emberSwal.fire("Oops...", e, "error");
  }

  defineErrors(e: Errors) {
    console.error(e);
    this.defineResourceCreationErrors(e);
  }

  /**
   * Errors returned by the API have the following format:
   *
   * {"errors":[
   *   {"title":"n'est pas disponible",
   *    "detail":"social-security-number - n'est pas disponible",
   *    "code":"100",
   *    "source":{"pointer":"/data/attributes/social-security-number"},
   *    "status":"422"}
   * ]}
   *
   * Error codes are defined [here](https://github.com/cerebris/jsonapi-resources/blob/c4242e32b78b9a31e81c2df153454da4a1e831bd/lib/jsonapi/error_codes.rb).
   */
  defineResourceCreationErrors(e: Errors) {
    let errorMessage;

    if (e.errors?.[0].status == "409") {
      const error = e.errors[0];
      // This is a validation error
      if (error.code == "100") {
        const attributeUniquenessErrorMessages = [
          "n'est pas disponible",
          "has already been taken",
          "est déjà utilisé(e)"
        ];

        if (attributeUniquenessErrorMessages.includes(error.title)) {
          const attribute = error.detail.split(" - ")[0];

          errorMessage = `Un patient avec le même ${this.intl.t(
            `patient.${attribute}`
          )} existe déjà. Si vous pensez ne pas avoir fait d’erreur de saisie, veuillez contacter le support pour vérification. <br /><a href="mailto:support@semeia.io">support@semeia.io</a>.`;
        }
      }
    }

    if (e.errors?.[0].status == "422") {
      const error = e.errors[0];
      // This is a validation error
      if (error.code == "100") {
        const patientDuplicateMessage = "may be a patient duplicate";

        if (patientDuplicateMessage.includes(error.title)) {
          errorMessage =
            "Un patient avec le même nom, le même prénom et la même date de naissance existe déjà.<br /><br />Merci de contacter support@semeia.io";
        }
      }
    }

    errorMessage ||= `Une erreur est survenue. Merci de contacter support@semeia.io.`;

    this.displayError(errorMessage);
  }

  showPendingChangesetError() {
    return emberSwal.fire({
      title: "Vous avez des changements non sauvegardés.",
      text: "Êtes-vous sûr de vouloir quitter ?",
      icon: "warning",
      cancelButtonText: "Annuler",
      showCancelButton: true,
      confirmButtonText: "Oui, quitter",
      confirmButtonColor: "#d33"
    });
  }
}
